import moment from "moment";

import { PatientEncounterType } from "../../models";
import { ValuesGridType } from "../../models/valuesGrid";
import {
  IPGroupingIsOutdated,
  OPGroupingIsOutdated,
  applyEncounterResult,
  collectChanges,
  mapEncounterToServiceEntity,
} from "../../services/encounter/encounterMapping";
import { checkFieldForCollection } from "../../utils/ddl";
import {
  applyPartialFacilityPreferences,
  isEncounterClosedError,
  needResequenceEncounterCodes,
  preparePartialFacilityPreferences,
  resequenceCodes,
} from "../../utils/encounter";

import {
  cloneEncounterEntity,
  createEmptyEncounter,
  createEmptyEncounterState,
  createEmptyServiceEncounter,
  updateCodes,
  updateValueCodes,
} from "./encounterCommon";

import type { EncounterEntity, EncounterState, ResequenceModes } from "../../models";
import type {
  EncounterProcessResult,
  EncounterSaveServiceResult,
  ServiceEncounterView,
} from "../../models/encounterServiceEntity";
import type { FacilityPreferences } from "../../models/facilitySettings";
import type { ChoiceListsState, PartialChoiceLists } from "../../models/patientEncounter";
import type { ErrorDetails } from "../../models/ui";

export const handleEncounterOpen = (
  state: EncounterState,
  payload: {
    encounterEntity: EncounterEntity;
    partialChoiceLists: PartialChoiceLists;
    choiceLists: ChoiceListsState;
    autoResequenceModes?: ResequenceModes;
    openEncounterToCodingTab?: boolean;
    facilityPreferences?: FacilityPreferences;
  },
  options: { isNewEncounter: boolean; isDirtyEncounter: boolean },
): EncounterState => {
  const { encounterEntity } = payload;
  const { facilities, encounterTypes } = payload.partialChoiceLists;
  let inProgress = cloneEncounterEntity(encounterEntity);
  inProgress.lastActiveCodeId = undefined;

  // focus admitDX code if coding tab is opened initially
  if (payload.openEncounterToCodingTab && document.hasFocus()) {
    inProgress.admitDiagnosisCode.inEdit = true;
    inProgress.admitDiagnosisCode.editField = "code";
    inProgress.admitDiagnosisCode.initialCode = inProgress.admitDiagnosisCode.code;
    inProgress.lastActiveCodeId = inProgress.admitDiagnosisCode.id;
  }

  const newState: EncounterState = {
    dirty: options.isDirtyEncounter,
    inProgress,
    saved: cloneEncounterEntity(encounterEntity),
    savedServiceEntity: mapEncounterToServiceEntity(encounterEntity, payload.choiceLists),
    needInitialProcess: false,
    // not use it according with GUI-1666: needInitialProcess: !options.isNewEncounter
  };

  // encounter type and facility cannot be empty. correct encounter type to the existing in this facility
  if (!inProgress.type) {
    inProgress.type =
      encounterTypes.length > 0 ? encounterTypes[0].ViewId || "" : PatientEncounterType.IP;
  }

  // use first encounter type if current encounter type is not existing in this facility
  const existingEncounterType = checkFieldForCollection(inProgress.type, encounterTypes);
  if (!existingEncounterType) {
    inProgress.type =
      encounterTypes.length > 0 ? encounterTypes[0].ViewId || "" : PatientEncounterType.IP;
  }

  if (!inProgress.facility) {
    inProgress.facility = facilities.length > 0 ? facilities[0].ViewId : "";
  }

  if (
    !options.isNewEncounter &&
    payload.autoResequenceModes &&
    needResequenceEncounterCodes(inProgress, payload.autoResequenceModes)
  ) {
    inProgress = resequenceCodes(inProgress, payload.autoResequenceModes);
    newState.dirty = true;
    newState.lastChangeTime = moment.now();
  }

  // copy necessary preferences to store them with encounter
  const partialFacilityPreferences = preparePartialFacilityPreferences(
    inProgress,
    payload.facilityPreferences,
  );
  // autoupdate encounter according with partial facility preferences
  if (applyPartialFacilityPreferences(inProgress, partialFacilityPreferences)) {
    newState.dirty = true;
    newState.lastChangeTime = moment.now();
  }

  // add empty codes for each grid to support adding
  updateCodes(inProgress);
  updateValueCodes(inProgress.conditionCodes, ValuesGridType.CONDITION_CODES);
  updateValueCodes(inProgress.valueCodes, ValuesGridType.VALUE_CODES);

  return {
    ...newState,
    inProgress,
    autoResequenceIgnored: false,
    codesAutoResequenced: false,
    checkIfCodesNeedResequencing: true,
    partialFacilityPreferences,
  };
};

// all work is on the backend side. We only update dirty flag
export const handleCurrentEncounterSaved = (
  state: EncounterState,
  newConcurrencyVersion: string,
): EncounterState => ({
  ...state,
  inProgress: {
    ...state.inProgress,
    concurrencyVersion: newConcurrencyVersion || state.inProgress.concurrencyVersion,
  },
  dirty: false,
});

// UI state saved to the backend. Syncronize states. All grids must be unlocked
export const handleEncounterUpdated = (
  state: EncounterState,
  result: EncounterSaveServiceResult,
  encounterToSave: EncounterEntity,
  serviceEncounterToSave: ServiceEncounterView,
  heartbeatMode = false,
  updateTime?: number,
): EncounterState => {
  // apply changes to the current encounter not to the saved
  const inProgress = applyEncounterResult(
    state.inProgress,
    { type: "ValidationResult", processResult: result },
    true,
  );
  updateCodes(inProgress);

  return {
    ...state,
    errorAttempt: 0,
    inProgress,
    lastErrorTime: undefined,
    lastUpdateTime: updateTime || moment.now(),
    saved: cloneEncounterEntity(encounterToSave),
    savedServiceEntity: serviceEncounterToSave,
    hangingProcessings: [], // need to process all if necessary
    checkIfCodesNeedResequencing: true,
  };
};

export const handleEncounterStartProcess = (state: EncounterState): EncounterState => ({
  ...state,
});

// Service fields were updated - update only them
export const handleEncounterProcessed = (
  state: EncounterState,
  payload: {
    results: EncounterProcessResult[];
    autoResequenceModes?: ResequenceModes;
    serviceEncounterToSave: ServiceEncounterView;
    choiceLists: ChoiceListsState;
    outdatedIPGrouping: string;
    outdatedOPGrouping: string;
    outdatedGroupings: string[];
  },
): EncounterState => {
  let { inProgress } = state;
  // changes from the last state to ignore auto resequence if we have changes. it will be executed next time
  const newChanges =
    payload.autoResequenceModes ?
      collectChanges(state.savedServiceEntity, inProgress, payload.choiceLists)
    : []; // payload.serviceEncounterToSave
  const hasNewchanges = newChanges && newChanges.length > 0;

  for (let ind = 0, len = payload.results.length; ind < len; ind++) {
    inProgress = applyEncounterResult(inProgress, payload.results[ind], false, true);
  }

  let codesAreResequenced = false;
  if (
    payload.autoResequenceModes &&
    !hasNewchanges &&
    needResequenceEncounterCodes(inProgress, payload.autoResequenceModes) &&
    !state.codesAutoResequenced
  ) {
    inProgress = resequenceCodes(inProgress, payload.autoResequenceModes);
    codesAreResequenced = true;
  }

  // check if any of oudatedGroupings has not be updated to not force theirs processing next time
  const hangingProcessings: string[] = [];
  if (payload.outdatedIPGrouping && IPGroupingIsOutdated(inProgress, payload.outdatedIPGrouping)) {
    hangingProcessings.push(payload.outdatedIPGrouping);
  }
  if (payload.outdatedOPGrouping && OPGroupingIsOutdated(inProgress, payload.outdatedOPGrouping)) {
    hangingProcessings.push(payload.outdatedOPGrouping);
  }

  updateCodes(inProgress);

  return {
    ...state,
    errorAttempt: 0,
    needInitialProcess: false,
    needRepeatFailedProcess: undefined,
    needHandling: false,
    hangingProcessings,
    inProgress,
    lastErrorTime: undefined,
    dirty: codesAreResequenced ? true : state.dirty,
    lastChangeTime: codesAreResequenced ? moment.now() : state.lastChangeTime,
    codesAutoResequenced: codesAreResequenced,
    autoResequenceIgnored: codesAreResequenced ? false : state.autoResequenceIgnored,
    checkIfCodesNeedResequencing: codesAreResequenced,
    // do not update saved here
  };
};

export const handleEncounterSetRepeatProcessFlag = (state: EncounterState): EncounterState => ({
  ...state,
  needRepeatFailedProcess: true,
});

// do not try to save incorrect encounter again. Unlock all codegroups
export const handleSaveEncounterChangesError = (
  state: EncounterState,
  payload: { errors: ErrorDetails[]; errorTime: number },
): EncounterState => ({
  ...state,
  closedByAnotherSession: !!(
    payload.errors &&
    payload.errors.length &&
    payload.errors[0].description &&
    isEncounterClosedError(payload.errors[0].description)
  ),
  errorAttempt: state.errorAttempt ? state.errorAttempt + 1 : 1,
  lastErrorTime: payload.errorTime || moment.now(),
  inProgress: {
    ...state.inProgress,
  },
});

export const handleSaveEncounterErrorUpdate = (state: EncounterState): EncounterState =>
  // clear error information for now
  ({
    ...state,
    errorAttempt: 0,
    lastErrorTime: undefined,
  });

export const handleIncorrectEncounter = (): EncounterState => createEmptyEncounterState();

export const handleCloseEncounter = () => ({
  dirty: false,
  inProgress: createEmptyEncounter(),
  saved: createEmptyEncounter(),
  savedServiceEntity: createEmptyServiceEncounter(),
});

export const handleMarkEncounterOutdated = (state: EncounterState) =>
  // clear concurrency version
  ({
    ...state,
    inProgress: {
      ...state.inProgress,
      concurrencyVersion: "",
    },
  });

export const handleSingleChoiceListUpdated = (state: EncounterState): EncounterState => state;
